import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SectionHeader, Button, Field, Loading, EmptyState, Tag } from "@/components/brand";
import { formatDayTime, formatR } from "@/lib/format";

const LEVELS = ["all", "beginner", "intermediate", "advanced"];
const DAYS = [["1", "Mon"], ["2", "Tue"], ["3", "Wed"], ["4", "Thu"], ["5", "Fri"], ["6", "Sat"], ["0", "Sun"]];

export default function TrainerSchedule() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const classes = useQuery({ queryKey: ["trainer", "classes"], queryFn: () => api("/trainer/classes") });
  const me = useQuery({ queryKey: ["trainer", "me"], queryFn: () => api("/trainer/me") });
  const approved = me.data?.status === "approved";

  const [form, setForm] = useState({ title: "", category: "hiit", description: "", start_time: "", duration_min: 45, price: 100, max_participants: 20, level: "all", equipment: "None" });
  const [repeat, setRepeat] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = useMutation({
    mutationFn: () => api("/trainer/classes", { method: "POST", body: {
      ...form, duration_min: Number(form.duration_min), price: Number(form.price), max_participants: Number(form.max_participants),
      start_time: new Date(form.start_time).toISOString(),
      repeat_days: repeat.length ? repeat.map(Number) : null, repeat_weeks: 4,
    } }),
    onSuccess: (r) => { setOpen(false); qc.invalidateQueries({ queryKey: ["trainer", "classes"] }); toast.success(`${r.length} class${r.length > 1 ? "es" : ""} created`); },
    onError: (e) => toast.error(e.message),
  });
  const cancel = useMutation({ mutationFn: (cid) => api(`/trainer/classes/${cid}/cancel`, { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["trainer", "classes"] }); toast.message("Class cancelled"); }, onError: (e) => toast.error(e.message) });

  const toggleDay = (d) => setRepeat((r) => r.includes(d) ? r.filter((x) => x !== d) : [...r, d]);

  return (
    <div data-testid="trainer-schedule-screen" className="space-y-5">
      <SectionHeader title="Schedule" subtitle="Create and manage your live classes." action="New class" onAction={() => setOpen(true)} testID="schedule-new-class" />
      {!approved ? <div className="p-4 rounded-xl2 bg-brand-soft font-body text-sm text-ink">Your profile must be approved before you can create classes.</div> : null}
      {classes.isLoading ? <Loading /> : (classes.data ?? []).length === 0 ? (
        <EmptyState icon={Plus} title="No classes yet" subtitle="Create your first live class." />
      ) : (
        <div className="space-y-2">
          {(classes.data ?? []).map((c) => (
            <div key={c.id} data-testid={`schedule-class-${c.id}`} className="flex items-center gap-3 p-3 rounded-xl2 border border-divider">
              <img src={c.image} alt="" className="w-14 h-14 rounded-md object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-display text-base text-ink truncate">{c.title}</span><Tag label={c.category} /></div>
                <div className="font-body text-xs text-mutedgrey">{formatDayTime(c.start_time)} · {c.duration_min}min · {c.booked_count}/{c.max_participants} booked</div>
              </div>
              <span className="font-display text-brand">{formatR(c.price)}</span>
              <button data-testid={`schedule-cancel-${c.id}`} onClick={() => cancel.mutate(c.id)} className="w-9 h-9 rounded-full bg-surface2 flex items-center justify-center text-mutedgrey hover:text-destructive"><X size={16} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg p-0 rounded-xl2 overflow-hidden" data-testid="create-class-dialog">
          <div className="p-5 space-y-3 max-h-[85vh] overflow-y-auto">
            <DialogTitle asChild><h2 className="font-display text-2xl text-ink">New Class</h2></DialogTitle>
            <Field label="Title" testID="class-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Morning HIIT" />
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1.5"><span className="font-body font-semibold text-[13px] text-ink">Category</span>
                <select data-testid="class-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="h-[52px] rounded-md border border-line bg-surface2 px-3 font-body text-[15px]">
                  {(cats.data ?? []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1.5"><span className="font-body font-semibold text-[13px] text-ink">Level</span>
                <select data-testid="class-level" value={form.level} onChange={(e) => set("level", e.target.value)} className="h-[52px] rounded-md border border-line bg-surface2 px-3 font-body text-[15px]">
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </label>
            </div>
            <Field label="Start time" testID="class-start" type="datetime-local" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Minutes" testID="class-duration" type="number" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} />
              <Field label="Price (R)" testID="class-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} />
              <Field label="Max" testID="class-max" type="number" value={form.max_participants} onChange={(e) => set("max_participants", e.target.value)} />
            </div>
            <Field label="Description" as="textarea" testID="class-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What to expect, what to bring…" />
            <div>
              <div className="font-body font-semibold text-[13px] text-ink mb-1.5">Repeat weekly for 4 weeks (optional)</div>
              <div className="flex gap-1.5 flex-wrap">
                {DAYS.map(([d, l]) => (
                  <button key={d} type="button" data-testid={`repeat-${l}`} onClick={() => toggleDay(d)} className={`w-11 h-9 rounded-md border font-body font-semibold text-xs ${repeat.includes(d) ? "bg-brand border-brand text-white" : "border-line text-ink"}`}>{l}</button>
                ))}
              </div>
            </div>
            <Button testID="class-create-submit" title="Create Class" loading={create.isPending} disabled={!form.title || !form.start_time} onClick={() => create.mutate()} full />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
